import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';

const SummaryDetails = ({ loadState }) => {
  const {
    compartments,
    deliveryStartDateTime,
    deliveryEndDateTime,
    driver,
    fillAll,
    location,
    poNumber,
    trailer,
  } = loadState;

  const formattedCreatedDate = format(new Date(), 'P');
  const formattedDeliveryStartDateTime = format(new Date(deliveryStartDateTime), 'Pp');
  const formattedDeliveryEndDateTime = format(new Date(deliveryEndDateTime), 'Pp');

  const filteredCompartments = R.reject(R.propEq('loadType', null), R.values(compartments));

  return (
    <Stack bgcolor="surface.medium">
      <Typography color="primary" sx={{ px: 3, py: 1 }} variant="subtitle1">
        Summary
      </Typography>

      <TitleBox label="Load Information" />
      <ItemBox label="Date Created" value={formattedCreatedDate} />
      <ItemBox label="Delivery Start" value={formattedDeliveryStartDateTime} />
      <ItemBox label="Delivery End" value={formattedDeliveryEndDateTime} />
      <ItemBox label="Internal PO Number" value={poNumber} />
      <ItemBox label="Driver" value={`${driver.lastName}, ${driver.firstName}`} />
      <ItemBox label="Trailer ID" value={trailer.vaporTightnessCode} />
      <ItemBox label="Terminal Location" value={location?.name.toUpperCase()} />

      {filteredCompartments.map(
        ({
          compartmentId,
          position,
          loadType,
          supplier,
          loadingControl,
          recipe,
          product,
          products,
          additives,
          volume,
          destination,
        }) => {
          const isCustomRecipe = R.propEq('loadTypeId', 1, loadType);
          const isHouseRecipe = R.propEq('loadTypeId', 2, loadType);
          const isRecipe = R.or(isCustomRecipe, isHouseRecipe);
          const isUnload = R.propEq('loadTypeId', 3, loadType);
          return (
            <Fragment key={compartmentId}>
              {fillAll ? (
                <TitleBox label="Compartment Information" />
              ) : (
                <TitleBox label={`Compartment #${position}`} />
              )}

              <ItemBox label="Load Type" value={loadType.name} />

              {isCustomRecipe && <ItemBox label="Supplier" value={supplier.supplierId} />}

              {isCustomRecipe && supplier.hasLoadingControls && (
                <ItemBox label="LCN" value={loadingControl?.loadingControlNumber} />
              )}

              {isCustomRecipe && (
                <ItemBox label="Recipe" value={`${recipe.recipeId}: ${recipe.name}`} />
              )}
              {isHouseRecipe && <ItemBox label="Recipe" value={recipe.name} />}

              {isHouseRecipe &&
                Object.values(products).map(
                  ({
                    productId,
                    productGradeCode,
                    supplier: productSupplier,
                    loadingControl: productLoadingControl,
                  }) => (
                    <Fragment key={productId}>
                      <ItemBox
                        label={`${productGradeCode} Grade Supplier`}
                        value={productSupplier.supplierId}
                      />
                      {productLoadingControl && (
                        <ItemBox
                          label={`${productGradeCode} Grade LCN`}
                          value={productLoadingControl?.loadingControlNumber ?? '--'}
                        />
                      )}
                    </Fragment>
                  )
                )}

              {additives.map(({ additiveId, name }, index) => (
                <ItemBox key={additiveId} label={`Additive #${index + 1}`} value={name} />
              ))}

              <ItemBox label="Volume" value={`${volume} Gallons`} />

              {isRecipe && <ItemBox label="Destination Name" value={destination.name} />}
              {isRecipe && <ItemBox label="Destination ID" value={destination.destinationId} />}
              {isRecipe && (
                <ItemBox
                  label="Destination Address"
                  value={`${destination.street ? `${destination.street}` : ''} 
                  ${destination.city}, ${destination.state ?? ''}
                  ${destination.zipCode ?? ''} 
                  `}
                />
              )}

              {isUnload && <ItemBox label="Supplier" value={supplier.supplierId} />}
              {isUnload && <ItemBox label="Product" value={product.name} />}
            </Fragment>
          );
        }
      )}
    </Stack>
  );
};

const TitleBox = ({ label }) => {
  return (
    <Box bgcolor="graphic.light" px={3} py={1} typography="subtitle1">
      {label}
    </Box>
  );
};

const ItemBox = ({ label, value }) => {
  return (
    <Stack
      borderBottom={1}
      borderBottomColor="graphic.light"
      direction="row"
      justifyContent="space-between"
      px={3}
      py={2}
    >
      <Typography color="text.secondary" noWrap sx={{ minWidth: 140 }} variant="body1">
        {label}
      </Typography>
      <Typography color="text.primary" textAlign="right" variant="subtitle1">
        {value}
      </Typography>
    </Stack>
  );
};

SummaryDetails.propTypes = {
  loadState: PropTypes.shape({
    compartments: PropTypes.shape({}).isRequired,
    deliveryEndDateTime: PropTypes.string.isRequired,
    deliveryStartDateTime: PropTypes.string.isRequired,
    driver: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }).isRequired,
    fillAll: PropTypes.bool.isRequired,
    location: PropTypes.shape({
      name: PropTypes.string,
    }).isRequired,
    poNumber: PropTypes.string.isRequired,
    trailer: PropTypes.shape({
      vaporTightnessCode: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

TitleBox.propTypes = {
  label: PropTypes.string.isRequired,
};

ItemBox.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
};

export default SummaryDetails;
